// pages/sales/SalesList.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Eye, 
    Edit, 
    Trash2, 
    PlusCircle,
    Download,
    Printer,
    Filter,
    Search,
    ArrowUp,
    ArrowDown,
    ChevronLeft,
    ChevronRight,    
    Users,
    File
} from 'react-feather';
import Select from 'react-select';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saleActions } from '../../core/action/saleActions';
import { companyActions } from '../../core/action/companyActions';
import ViewSale from '../../core/modals/sales/viewSale';
import EditSale from '../../core/modals/sales/EditSale';

const { RangePicker } = DatePicker;

const SalesList = () => {
    const navigate = useNavigate();
    
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [companySettings, setCompanySettings] = useState({});
    
    // Filters
    const [filters, setFilters] = useState({
        status: '',
        payment_status: '',
        customer_id: 0,
        date_from: '',
        date_to: ''
    });
    
    // Pagination
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });
    
    // Modal state
    const [modalType, setModalType] = useState(null); // 'view', 'edit'
    const [selectedSale, setSelectedSale] = useState(null);
    
    // Sort
    const [sortConfig, setSortConfig] = useState({ 
        key: 'sale_date', 
        direction: 'desc'
    });

    const isInitialMount = useRef(true);
    const prevPageRef = useRef(1);

    // Load company settings
    const loadCompanySettings = async () => {
        try {
            const result = await companyActions.getSettings();
            if (result.success) {
                setCompanySettings(result.data || {});
            }
        } catch (error) {
            console.error('Error loading company settings:', error);
        }
    };

    useEffect(() => {
        loadCompanySettings();
    }, []);

    // Status options
    const statusOptions = [
        { value: '', label: 'All Status' },
        { value: 'pending', label: 'Pending' },
        { value: 'confirmed', label: 'Confirmed' },
        { value: 'processing', label: 'Processing' },
        { value: 'shipped', label: 'Shipped' },
        { value: 'delivered', label: 'Delivered' },
        { value: 'cancelled', label: 'Cancelled' }
    ];

    // Payment status options
    const paymentStatusOptions = [
        { value: '', label: 'All Payment' },
        { value: 'unpaid', label: 'Unpaid' },
        { value: 'partial', label: 'Partial' },
        { value: 'paid', label: 'Paid' }
    ];

    // Customer options
    const [customers, setCustomers] = useState([]);

    // Load customers
    const loadCustomers = async () => {
        try {
            const result = await saleActions.getCustomers({ limit: 1000 });
            if (result.success) {
                setCustomers([
                    { value: 0, label: 'All Customers' },
                    ...result.data.map(c => ({
                        value: c.id,
                        label: `${c.name} (${c.phone || 'No phone'})`
                    }))
                ]);
            }
        } catch (error) {
            console.error('Error loading customers:', error);
        }
    };

    useEffect(() => {
        loadCustomers();
    }, []);

    // Main fetch function
    const fetchSales = async (page = pagination.page) => {
        try {
            setLoading(true);
            
            const params = {
                page: page,
                limit: pagination.limit,
                sort_by: sortConfig.key,
                sort_order: sortConfig.direction.toUpperCase(),
                ...(searchTerm && { search: searchTerm }),
                ...(filters.status && { status: filters.status }),
                ...(filters.payment_status && { payment_status: filters.payment_status }),
                ...(filters.customer_id > 0 && { customer_id: filters.customer_id }),
                ...(filters.date_from && { date_from: filters.date_from }),
                ...(filters.date_to && { date_to: filters.date_to })
            };
            
            console.log('Fetching sales with params:', params);
            
            const result = await saleActions.getSales(params);
            
            console.log('API Response:', result);
            
            if (result.success) {
                setSales(result.data || []);
                setPagination(prev => ({
                    ...prev,
                    page: result.page || page,
                    total: result.total,
                    totalPages: result.totalPages || Math.ceil(result.total / pagination.limit)
                }));
                prevPageRef.current = page;
            } else {
                Swal.fire('Error', result.message || 'Failed to load sales', 'error');
                setSales([]);
            }
        } catch (error) {
            console.error('Error fetching sales:', error);
            Swal.fire('Error', 'Failed to load sales', 'error');
            setSales([]);
        } finally {
            setLoading(false);
        }
    };

    // Initial fetch
    useEffect(() => {
        fetchSales(1);
        isInitialMount.current = false;
    }, []);

    // Fetch when page changes
    useEffect(() => {
        if (isInitialMount.current || prevPageRef.current === pagination.page) return;
        fetchSales(pagination.page);
    }, [pagination.page]);

    // Fetch when filters or sort change
    useEffect(() => {
        if (isInitialMount.current) return;
        
        const timer = setTimeout(() => {
            setPagination(prev => ({ ...prev, page: 1 }));
            setTimeout(() => fetchSales(1), 0);
        }, 300);
        
        return () => clearTimeout(timer);
    }, [sortConfig, searchTerm, filters]);

    // Handle sort
    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    // Modal handlers
    const handleView = (sale) => {
        setSelectedSale(sale);
        setModalType('view');
    };

    const handleEdit = (sale) => {
        setSelectedSale(sale);
        setModalType('edit');
    };

    const handleModalClose = () => {
        setModalType(null);
        setSelectedSale(null);
    };

    const handleSuccess = () => {
        fetchSales(pagination.page);
        handleModalClose();
    };

    // Handle delete
    const handleDelete = async (id) => {
        Swal.fire({
            title: 'Delete Sale?',
            text: 'Are you sure you want to delete this sale? This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const deleteResult = await saleActions.deleteSale(id);
                    if (deleteResult.success) {
                        Swal.fire('Deleted!', 'Sale has been deleted.', 'success');
                        fetchSales(pagination.page);
                    } else {
                        Swal.fire('Error', deleteResult.message || 'Failed to delete sale', 'error');
                    }
                } catch (error) {
                    console.error('Error deleting sale:', error);
                    Swal.fire('Error', 'Failed to delete sale', 'error');
                }
            }
        });
    };

    // PDF Download Function
    const downloadSalePDF = async (sale) => {
        try {
            setLoading(true);

            Swal.fire({
                title: 'Generating PDF...',
                text: 'Please wait',
                allowOutsideClick: false,
                didOpen: () => { Swal.showLoading(); }
            });

            // Fetch complete sale details with items
            const result = await saleActions.getSale(sale.id);

            if (!result.success || !result.data) {
                Swal.fire({ icon: 'error', title: 'Error', text: result?.message || 'Failed to load sale details', timer: 2000 });
                return;
            }

            const fullSale = result.data;
            const items = fullSale.items || [];

            const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const pageWidth = doc.internal.pageSize.width;
            const pageHeight = doc.internal.pageSize.height;
            const margin = 15;
            let yPos = 0;

            // COLOR PALETTE
            const BRAND = [30, 80, 160];
            const BRAND_LIGHT = [235, 241, 255];
            const ACCENT = [40, 167, 69];
            const DARK = [30, 30, 40];
            const MUTED = [110, 110, 125];
            const LIGHT_GRAY = [245, 245, 248];
            const BORDER = [210, 215, 225];
            const WHITE = [255, 255, 255];

            // HELPER: currency (Rs. - jsPDF Helvetica cannot render the rupee symbol glyph)
            const fmt = (n) => `Rs. ${parseFloat(n || 0).toFixed(2)}`;

            // HEADER BANNER
            doc.setFillColor(...BRAND);
            doc.rect(0, 0, pageWidth, 28, 'F');



            // Company Logo
            if (companySettings && companySettings.company_logo) {
                try {
                    let logoUrl = companySettings.company_logo;
                    if (!logoUrl.startsWith('http')) logoUrl = `${window.location.origin}/${logoUrl.replace(/^\/+/, '')}`;
                    doc.addImage(logoUrl, 'PNG', margin, 5, 30, 10, undefined, 'FAST');
                } catch (e) { /* skip */ }
            }

            // Company name in banner
            doc.setFontSize(15);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...WHITE);
            const compName = (companySettings && companySettings.company_name) ? companySettings.company_name : 'Your Company Name';
            doc.text(compName, margin + 33, 12);

            // GSTIN in banner
            if (companySettings && companySettings.gst_number) {
                doc.setFontSize(7.5);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(200, 215, 255);
                doc.text(`GSTIN: ${companySettings.gst_number}`, margin + 33, 18);
            }

            // Address & contact in banner (right-aligned)
            const rightX = pageWidth - margin;
            let bannerRy = 9;
            doc.setFontSize(7.5);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(210, 220, 255);
            if (companySettings && companySettings.company_phone) { doc.text(`Ph: ${companySettings.company_phone}`, rightX, bannerRy, { align: 'right' }); bannerRy += 4.5; }
            if (companySettings && companySettings.company_email) { doc.text(`Email: ${companySettings.company_email}`, rightX, bannerRy, { align: 'right' }); bannerRy += 4.5; }
            if (companySettings && companySettings.company_address) {
                const adr = doc.splitTextToSize(companySettings.company_address, 70);
                doc.text(adr[0], rightX, bannerRy, { align: 'right' });
            }

            // Green accent bar below banner
            doc.setFillColor(...ACCENT);
            doc.rect(0, 28, pageWidth, 2, 'F');

            yPos = 38;

            // DOC TITLE ROW
            doc.setFontSize(20);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...BRAND);
            doc.text('TAX INVOICE', margin, yPos);

            // Meta pills on right
            const invoiceNo = fullSale.invoice_no || sale.invoice_no || 'N/A';
            const invoiceDate = fullSale.sale_date || sale.sale_date || null;
            const dcNumber = fullSale.dc_number || sale.dc_number;
            const poNumber = fullSale.po_number || sale.po_number;

            const metaRightX = pageWidth - margin;
            let metaY = yPos - 5;

            // Pill: Invoice No
            doc.setFillColor(...BRAND_LIGHT);
            doc.roundedRect(metaRightX - 80, metaY, 80, 7, 1.5, 1.5, 'F');
            doc.setFontSize(8);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...BRAND);
            doc.text('Invoice No:', metaRightX - 77, metaY + 4.8);
            doc.setFont('helvetica', 'normal');
            doc.text(invoiceNo, metaRightX - 2, metaY + 4.8, { align: 'right' });
            metaY += 9;

            // Pill: Date
            doc.setFillColor(...LIGHT_GRAY);
            doc.roundedRect(metaRightX - 80, metaY, 80, 7, 1.5, 1.5, 'F');
            doc.setFontSize(8);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...MUTED);
            doc.text('Date:', metaRightX - 77, metaY + 4.8);
            doc.setFont('helvetica', 'normal');
            doc.text(invoiceDate ? dayjs(invoiceDate).format('DD MMM YYYY') : 'N/A', metaRightX - 2, metaY + 4.8, { align: 'right' });
            metaY += 9;

            // Pill: DC Number
            if (dcNumber) {
                doc.setFillColor(...LIGHT_GRAY);
                doc.roundedRect(metaRightX - 80, metaY, 80, 7, 1.5, 1.5, 'F');
                doc.setFontSize(8);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(...MUTED);
                doc.text('DC No:', metaRightX - 77, metaY + 4.8);
                doc.setFont('helvetica', 'normal');
                doc.text(String(dcNumber), metaRightX - 2, metaY + 4.8, { align: 'right' });
                metaY += 9;
            }

            // Pill: PO Number
            if (poNumber) {
                doc.setFillColor(...LIGHT_GRAY);
                doc.roundedRect(metaRightX - 80, metaY, 80, 7, 1.5, 1.5, 'F');
                doc.setFontSize(8);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(...MUTED);
                doc.text('PO No:', metaRightX - 77, metaY + 4.8);
                doc.setFont('helvetica', 'normal');
                doc.text(String(poNumber), metaRightX - 2, metaY + 4.8, { align: 'right' });
                metaY += 9;
            }

            yPos += 10;

            // Thin full-width rule
            doc.setDrawColor(...BORDER);
            doc.setLineWidth(0.3);
            doc.line(margin, yPos, pageWidth - margin, yPos);
            yPos += 6;

            // BILL TO SECTION
            doc.setFillColor(...BRAND);
            doc.roundedRect(margin, yPos, (pageWidth - margin * 2) * 0.55, 6.5, 1, 1, 'F');
            doc.setFontSize(8.5);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...WHITE);
            doc.text('BILL TO', margin + 3, yPos + 4.5);
            yPos += 9;

            const custBlockW = (pageWidth - margin * 2) * 0.55;
            const custBlockStartY = yPos;

            const customerName = fullSale.customer_name || sale.customer_name || 'N/A';
            const customerPhone = fullSale.customer_phone || sale.customer_phone;
            const customerEmail = fullSale.customer_email || sale.customer_email;
            const customerGst = fullSale.customer_gst || sale.customer_gst;
            const customerAddress = fullSale.customer_address || sale.customer_address;

            // Measure height for customer block first
            let custY = yPos + 3;
            doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(...DARK);
            doc.text(customerName, margin + 2, custY); custY += 5.5;
            doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(...MUTED);
            if (customerPhone) { doc.text(`Ph: ${customerPhone}`, margin + 2, custY); custY += 4.5; }
            if (customerEmail) { doc.text(`Email: ${customerEmail}`, margin + 2, custY); custY += 4.5; }
            if (customerGst) { doc.text(`GSTIN: ${customerGst}`, margin + 2, custY); custY += 4.5; }
            if (customerAddress) {
                const addrLines = doc.splitTextToSize(`Addr: ${customerAddress}`, custBlockW - 5);
                doc.text(addrLines, margin + 2, custY);
                custY += addrLines.length * 4.5;
            }

            // Draw background then redraw text on top
            doc.setFillColor(...LIGHT_GRAY);
            doc.roundedRect(margin, custBlockStartY, custBlockW, custY - custBlockStartY + 3, 1.5, 1.5, 'F');

            custY = custBlockStartY + 3;
            doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(...DARK);
            doc.text(customerName, margin + 3, custY); custY += 5.5;
            doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(...MUTED);
            if (customerPhone) { doc.text(`Ph: ${customerPhone}`, margin + 3, custY); custY += 4.5; }
            if (customerEmail) { doc.text(`Email: ${customerEmail}`, margin + 3, custY); custY += 4.5; }
            if (customerGst) { doc.text(`GSTIN: ${customerGst}`, margin + 3, custY); custY += 4.5; }
            if (customerAddress) {
                const al = doc.splitTextToSize(`Addr: ${customerAddress}`, custBlockW - 6);
                doc.text(al, margin + 3, custY);
                custY += al.length * 4.5;
            }

            yPos = custY + 8;

            // ITEMS TABLE
            const subtotal = parseFloat(fullSale.subtotal || sale.subtotal || 0);
            const tax = parseFloat(fullSale.tax || sale.tax || 0);
            const discount = parseFloat(fullSale.discount || sale.discount || 0);
            const shipping = parseFloat(fullSale.courier_charge || fullSale.shipping || sale.courier_charge || sale.shipping || 0);
            const additionalCharge = parseFloat(fullSale.additional_charge || sale.additional_charge || 0);
            const adjustment = parseFloat(fullSale.adjustment || sale.adjustment || 0);
            const roundOff = parseFloat(fullSale.round_off || sale.round_off || 0);
            const grandTotal = parseFloat(fullSale.grand_total || sale.grand_total || 0);
            const splDiscPercent = parseFloat(fullSale.spl_disc_percent || sale.spl_disc_percent || 0);
            const splDiscAmount = parseFloat(fullSale.spl_disc_amount || sale.spl_disc_amount || 0);
            const cgst = parseFloat(fullSale.cgst || sale.cgst || tax / 2);
            const sgst = parseFloat(fullSale.sgst || sale.sgst || tax / 2);

            // Show items in reverse order: last-added item appears first
            const orderedItems = items && items.length > 0 ? [...items].reverse() : [];
            const tableBody = [];
            if (orderedItems.length > 0) {
                orderedItems.forEach((item, index) => {
                    const productName = item.product_name || 'Product';
                    const skuCode = item.sku || item.product_code || '-';
                    const qty = parseFloat(item.quantity) || 1;
                    const unitPrice = parseFloat(item.unit_price) || 0;
                    const discountPct = parseFloat(item.discount) || 0;
                    const taxPct = parseFloat(item.tax_percent) || 0;
                    const totalAmount = parseFloat(item.total_cost) || 0;
                    tableBody.push([
                        (index + 1).toString(),
                        productName,
                        skuCode,
                        qty.toString(),
                        unitPrice.toFixed(2),
                        `${discountPct}%`,
                        `${taxPct}%`,
                        totalAmount.toFixed(2)
                    ]);
                });
            } else {
                tableBody.push(['1', 'No items found', '-', '0', '0.00', '0%', '0%', '0.00']);
            }

            autoTable(doc, {
                startY: yPos,
                head: [['#', 'Product Description', 'SKU', 'Qty', 'Unit Price', 'Disc%', 'Tax%', 'Amount']],
                body: tableBody,
                theme: 'plain',
                headStyles: {
                    fillColor: BRAND,
                    textColor: WHITE,
                    fontStyle: 'bold',
                    fontSize: 8.5,
                    halign: 'center',
                    cellPadding: { top: 3, bottom: 3, left: 2, right: 2 },
                    lineColor: BRAND,
                    lineWidth: 0
                },
                bodyStyles: {
                    fontSize: 8,
                    textColor: DARK,
                    cellPadding: { top: 2.5, bottom: 2.5, left: 2, right: 2 },
                    lineColor: BORDER,
                    lineWidth: 0.2
                },
                alternateRowStyles: { fillColor: [248, 249, 255] },
                rowPageBreak: 'auto',
                columnStyles: {
                    0: { cellWidth: 8, halign: 'center' },
                    1: { cellWidth: 58, halign: 'left' },
                    2: { cellWidth: 20, halign: 'left' },
                    3: { cellWidth: 14, halign: 'center' },
                    4: { cellWidth: 22, halign: 'right' },
                    5: { cellWidth: 12, halign: 'center' },
                    6: { cellWidth: 12, halign: 'center' },
                    7: { cellWidth: 24, halign: 'right' }
                },
                margin: { left: margin, right: margin },
                tableWidth: 'auto',
                didDrawPage: () => {
                    doc.setFillColor(...ACCENT);
                    doc.rect(0, 28, pageWidth, 2, 'F');
                }
            });

            // SUMMARY SECTION
            const tableEndY = doc.lastAutoTable.finalY;
            const summaryW = 80;
            const summaryX = pageWidth - margin - summaryW;
            let summaryY = tableEndY + 6;

            const summaryRows = [
                { label: 'Subtotal',          value: fmt(subtotal),  bold: false },
                { label: 'CGST',              value: fmt(cgst),      bold: false },
                { label: 'SGST',              value: fmt(sgst),      bold: false },
                { label: 'Discount',          value: `- ${fmt(discount)}`, bold: false, color: [220, 53, 69] },
                { label: splDiscPercent > 0 ? `Spl Disc (${splDiscPercent}%)` : 'Spl Disc', value: `- ${fmt(splDiscAmount)}`, bold: false, color: [220, 53, 69] },
                { label: 'Courier / Shipping',  value: fmt(shipping),  bold: false },
                { label: 'Additional Charge', value: fmt(additionalCharge), bold: false },
                { label: 'Adjustment',        value: (adjustment >= 0 ? '+ ' : '- ') + fmt(Math.abs(adjustment)), bold: false, color: adjustment >= 0 ? [40, 167, 69] : [220, 53, 69] },
                { label: 'Round Off',         value: (roundOff >= 0 ? '+ ' : '') + fmt(roundOff), bold: false, color: [110, 110, 125] },
            ];

            const rowH = 7;
            const totalCardH = summaryRows.length * rowH + 13;

            // Card shadow
            doc.setFillColor(220, 225, 240);
            doc.roundedRect(summaryX + 1, summaryY + 1, summaryW, totalCardH, 2, 2, 'F');

            // Card background
            doc.setFillColor(...WHITE);
            doc.roundedRect(summaryX, summaryY, summaryW, totalCardH, 2, 2, 'F');
            doc.setDrawColor(...BORDER);
            doc.setLineWidth(0.3);
            doc.roundedRect(summaryX, summaryY, summaryW, totalCardH, 2, 2, 'S');

            // Card header
            doc.setFillColor(...BRAND_LIGHT);
            doc.roundedRect(summaryX, summaryY, summaryW, 7, 2, 2, 'F');
            doc.rect(summaryX, summaryY + 3.5, summaryW, 3.5, 'F');
            doc.setFontSize(8.5);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...BRAND);
            doc.text('Summary', summaryX + summaryW / 2, summaryY + 5, { align: 'center' });
            summaryY += 9;

            // Summary rows
            summaryRows.forEach((row, i) => {
                if (i % 2 === 0) {
                    doc.setFillColor(...LIGHT_GRAY);
                    doc.rect(summaryX, summaryY - 1.5, summaryW, rowH, 'F');
                }
                doc.setFontSize(8);
                doc.setFont('helvetica', row.bold ? 'bold' : 'normal');
                doc.setTextColor(...MUTED);
                doc.text(row.label + ':', summaryX + 3, summaryY + 3.5);
                doc.setTextColor(...(row.color || DARK));
                doc.text(row.value, summaryX + summaryW - 3, summaryY + 3.5, { align: 'right' });
                summaryY += rowH;
            });

            // Divider
            doc.setDrawColor(...BORDER);
            doc.setLineWidth(0.4);
            doc.line(summaryX + 3, summaryY, summaryX + summaryW - 3, summaryY);
            summaryY += 3;

            // Grand Total row — BRAND blue matching quotation style
            doc.setFillColor(...BRAND);
            doc.roundedRect(summaryX, summaryY - 1, summaryW, 11, 0, 0, 'F');
            doc.rect(summaryX, summaryY - 1, summaryW, 6, 'F');
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...WHITE);
            doc.text('Grand Total', summaryX + 3, summaryY + 6);
            doc.text(fmt(grandTotal), summaryX + summaryW - 3, summaryY + 6, { align: 'right' });

            // NOTES / REMARKS (left side, beside summary card)
            const notesMaxW = summaryX - margin - 5;
            let notesY = tableEndY + 8;
            const remarks = fullSale.remarks || sale.remarks;
            if (remarks) {
                doc.setFontSize(9);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(...DARK);
                doc.text('Remarks:', margin, notesY);
                notesY += 5;
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(8);
                doc.setTextColor(...MUTED);
                const rl = doc.splitTextToSize(remarks, notesMaxW);
                doc.text(rl, margin, notesY);
                notesY += rl.length * 4.5 + 4;
            }

            // PAYMENT INFORMATION BLOCK
            const paymentStatus = fullSale.payment_status || sale.payment_status || 'unpaid';
            const paidAmount = parseFloat(fullSale.paid_amount || sale.paid_amount || 0);
            const dueAmount = parseFloat(fullSale.due_amount || sale.due_amount || 0);
            const paymentMode = fullSale.payment_mode || sale.payment_mode;

            let paymentY = summaryY + 15;
            if (paymentY > pageHeight - 50) {
                doc.addPage();
                paymentY = 30;
            }

            const paymentBlockW = 100;
            doc.setFillColor(...BRAND_LIGHT);
            doc.roundedRect(margin, paymentY, paymentBlockW, 30, 2, 2, 'F');
            doc.setDrawColor(...BORDER);
            doc.setLineWidth(0.3);
            doc.roundedRect(margin, paymentY, paymentBlockW, 30, 2, 2, 'S');

            doc.setFontSize(9);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...BRAND);
            doc.text('Payment Information', margin + 3, paymentY + 6);

            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...MUTED);
            doc.text('Status:', margin + 3, paymentY + 13);
            const psColor = paymentStatus === 'paid' ? [40, 167, 69] : paymentStatus === 'partial' ? [255, 140, 0] : [220, 53, 69];
            doc.setTextColor(...psColor);
            doc.text(paymentStatus.toUpperCase(), margin + 30, paymentY + 13);

            doc.setTextColor(...MUTED);
            doc.text('Paid Amount:', margin + 3, paymentY + 20);
            doc.setTextColor(...DARK);
            doc.text(fmt(paidAmount), margin + 30, paymentY + 20);

            doc.setTextColor(...MUTED);
            doc.text('Due Amount:', margin + 3, paymentY + 27);
            doc.setTextColor(...(dueAmount > 0 ? [220, 53, 69] : [40, 167, 69]));
            doc.text(fmt(dueAmount), margin + 30, paymentY + 27);

            if (paymentMode) {
                doc.setTextColor(...MUTED);
                doc.text('Mode:', margin + 60, paymentY + 13);
                doc.setTextColor(...DARK);
                doc.text(paymentMode.toUpperCase(), margin + 78, paymentY + 13);
            }

            // FOOTER
            const totalPages = doc.internal.getNumberOfPages();
            for (let p = 1; p <= totalPages; p++) {
                doc.setPage(p);
                doc.setFillColor(...BRAND);
                doc.rect(0, pageHeight - 12, pageWidth, 12, 'F');
                doc.setFontSize(7);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(200, 215, 255);
                doc.text('This is a computer-generated invoice — valid with digital signature.', margin, pageHeight - 5);
                doc.text(
                    `Page ${p} of ${totalPages}  |  Generated: ${dayjs().format('DD MMM YYYY, HH:mm')}`,
                    pageWidth - margin,
                    pageHeight - 5,
                    { align: 'right' }
                );
                doc.setFillColor(...ACCENT);
                doc.rect(0, pageHeight - 13, pageWidth, 1, 'F');
            }

            Swal.close();

            const fileName = `Invoice_${invoiceNo.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
            doc.save(fileName);

            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Invoice PDF downloaded successfully',
                timer: 1500,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Error generating PDF:', error);
            Swal.close();
            Swal.fire({
                icon: 'error',
                title: 'PDF Generation Failed',
                text: error.message || 'Unknown error occurred',
                confirmButtonText: 'OK'
            });
        } finally {
            setLoading(false);
        }
    };

    // Handle page change
    const handlePageChange = (pageNumber) => {
        if (pageNumber !== pagination.page) {
            setPagination(prev => ({ ...prev, page: pageNumber }));
        }
    };

    const handleNextPage = () => {
        if (pagination.page < pagination.totalPages) {
            setPagination(prev => ({ ...prev, page: prev.page + 1 }));
        }
    };

    const handlePrevPage = () => {
        if (pagination.page > 1) {
            setPagination(prev => ({ ...prev, page: prev.page - 1 }));
        }
    };

    // Handle search
    const handleSearchInput = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            setPagination(prev => ({ ...prev, page: 1 }));
        }
    };

    // Reset filters
    const resetFilters = () => {
        setSearchTerm('');
        setFilters({
            status: '',
            payment_status: '',
            customer_id: 0,
            date_from: '',
            date_to: ''
        });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Handle date range change
    const handleDateRangeChange = (dates) => {
        if (dates && dates.length === 2) {
            setFilters(prev => ({
                ...prev,
                date_from: dates[0].format('YYYY-MM-DD'),
                date_to: dates[1].format('YYYY-MM-DD')
            }));
        } else {
            setFilters(prev => ({
                ...prev,
                date_from: '',
                date_to: ''
            }));
        }
    };

    // Handle filter change
    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({ ...prev, [filterName]: value }));
    };

    // Format functions
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return dayjs(dateString).format('DD/MM/YYYY');
    };

    const getStatusBadge = (status) => {
        const badges = {
            'pending': 'badge bg-warning',
            'confirmed': 'badge bg-info',
            'processing': 'badge bg-primary',
            'shipped': 'badge bg-secondary',
            'delivered': 'badge bg-success',
            'cancelled': 'badge bg-danger'
        };
        return badges[status?.toLowerCase()] || 'badge bg-secondary';
    };

    const getPaymentStatusBadge = (status) => {
        const badges = {
            'unpaid': 'badge bg-danger',
            'partial': 'badge bg-warning',
            'paid': 'badge bg-success'
        };
        return badges[status?.toLowerCase()] || 'badge bg-secondary';
    };

    // Calculate statistics
    const calculateStats = () => {
        const totalSales = pagination.total;
        const totalAmount = sales.reduce((sum, s) => sum + parseFloat(s.grand_total || 0), 0);
        const paidAmount = sales.reduce((sum, s) => sum + parseFloat(s.paid_amount || 0), 0);
        const dueAmount = sales.reduce((sum, s) => sum + parseFloat(s.due_amount || 0), 0);
        const pendingSales = sales.filter(s => s.status === 'pending').length;
        const deliveredSales = sales.filter(s => s.status === 'delivered').length;

        return {
            totalSales,
            totalAmount: totalAmount.toFixed(2),
            paidAmount: paidAmount.toFixed(2),
            dueAmount: dueAmount.toFixed(2),
            pendingSales,
            deliveredSales
        };
    };

    const stats = calculateStats();

    // Calculate current page items indices
    const indexOfFirstItem = sales.length > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0;
    const indexOfLastItem = sales.length > 0 ? Math.min(pagination.page * pagination.limit, pagination.total) : 0;

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        <h4>Sales Management</h4>
                        <h6>Manage your sales invoices and track payments</h6>
                    </div>
                    <ul className="table-top-head">
                        <li>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => navigate('/add-sales')}
                            >
                                <PlusCircle className="me-2" size={18} />
                                New Sale
                            </button>
                        </li>
                        <li>
                            <button className="btn btn-outline-primary">
                                <Download className="me-2" size={18} />
                                Export
                            </button>
                        </li>
                        <li>
                            <button className="btn btn-outline-secondary">
                                <Printer className="me-2" size={18} />
                                Print
                            </button>
                        </li>
                    </ul>
                </div>

                   {/* Statistics Cards */}
                <div className="row mb-4">
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Total Sales</h5>
                                <h3 className="card-text text-primary">{stats.totalSales}</h3>
                                <p className="card-text text-muted">All time</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Sale Amount</h5>
                                <h3 className="card-text text-success">{formatCurrency(stats.totalAmount)}</h3>
                                <p className="card-text text-muted">Grand total</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Paid Amount</h5>
                                <h3 className="card-text text-info">{formatCurrency(stats.paidAmount)}</h3>
                                <p className="card-text text-muted">Amount received</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Due Amount</h5>
                                <h3 className="card-text text-danger">{formatCurrency(stats.dueAmount)}</h3>
                                <p className="card-text text-muted">Pending payment</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Pending</h5>
                                <h3 className="card-text text-warning">{stats.pendingSales}</h3>
                                <p className="card-text text-muted">Pending sales</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Delivered</h5>
                                <h3 className="card-text text-success">{stats.deliveredSales}</h3>
                                <p className="card-text text-muted">Delivered sales</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-lg-3 col-md-6 mb-3">
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <Search size={18} />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search by invoice, customer..."
                                        value={searchTerm}
                                        onChange={handleSearchInput}
                                        onKeyPress={handleKeyPress}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-2 col-md-3 col-sm-6 mb-3">
                                <Select
                                    options={statusOptions}
                                    value={statusOptions.find(opt => opt.value === filters.status)}
                                    onChange={(opt) => handleFilterChange('status', opt?.value || '')}
                                    placeholder="All Status"
                                    isClearable
                                />
                            </div>
                            <div className="col-lg-2 col-md-3 col-sm-6 mb-3">
                                <Select
                                    options={paymentStatusOptions}
                                    value={paymentStatusOptions.find(opt => opt.value === filters.payment_status)}
                                    onChange={(opt) => handleFilterChange('payment_status', opt?.value || '')}
                                    placeholder="All Payment"
                                    isClearable
                                />
                            </div>
                            <div className="col-lg-2 col-md-3 col-sm-6 mb-3">
                                <Select
                                    options={customers}
                                    value={customers.find(c => c.value === filters.customer_id)}
                                    onChange={(opt) => handleFilterChange('customer_id', opt?.value || 0)}
                                    placeholder="All Customers"
                                    isClearable
                                />
                            </div>
                            <div className="col-lg-3 col-md-6 mb-3">
                                <RangePicker
                                    className="form-control"
                                    format="DD/MM/YYYY"
                                    onChange={handleDateRangeChange}
                                    placeholder={['From Date', 'To Date']}
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <div className="col-lg-12 text-end">
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={resetFilters}
                                >
                                    <Filter className="me-2" size={18} />
                                    Reset Filters
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sales Table */}
                <div className="card">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center p-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3">Loading sales...</p>
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead>
                                            <tr>
                                                <th onClick={() => handleSort('invoice_no')} style={{ cursor: 'pointer' }}>
                                                    Invoice No
                                                    {sortConfig.key === 'invoice_no' && (
                                                        sortConfig.direction === 'asc' ? 
                                                        <ArrowUp className="ms-1" size={14} /> : 
                                                        <ArrowDown className="ms-1" size={14} />
                                                    )}
                                                </th>
                                                <th onClick={() => handleSort('sale_date')} style={{ cursor: 'pointer' }}>
                                                    Date
                                                    {sortConfig.key === 'sale_date' && (
                                                        sortConfig.direction === 'asc' ? 
                                                        <ArrowUp className="ms-1" size={14} /> : 
                                                        <ArrowDown className="ms-1" size={14} />
                                                    )}
                                                </th>
                                                <th onClick={() => handleSort('customer_name')} style={{ cursor: 'pointer' }}>
                                                    Customer
                                                    {sortConfig.key === 'customer_name' && (
                                                        sortConfig.direction === 'asc' ? 
                                                        <ArrowUp className="ms-1" size={14} /> : 
                                                        <ArrowDown className="ms-1" size={14} />
                                                    )}
                                                </th>
                                                <th onClick={() => handleSort('grand_total')} style={{ cursor: 'pointer' }}>
                                                    Amount
                                                    {sortConfig.key === 'grand_total' && (
                                                        sortConfig.direction === 'asc' ? 
                                                        <ArrowUp className="ms-1" size={14} /> : 
                                                        <ArrowDown className="ms-1" size={14} />
                                                    )}
                                                </th>
                                                <th>Status</th>
                                                <th>Payment</th>
                                                <th>Payment Info</th>
                                                <th onClick={() => handleSort('due_amount')} style={{ cursor: 'pointer' }}>
                                                    Due Amount
                                                    {sortConfig.key === 'due_amount' && (
                                                        sortConfig.direction === 'asc' ? 
                                                        <ArrowUp className="ms-1" size={14} /> : 
                                                        <ArrowDown className="ms-1" size={14} />
                                                    )}
                                                </th>
                                                <th>Actions</th>
                                            </tr>
                                            </thead>
                                        <tbody>
                                            {sales.length === 0 ? (
                                                <tr>
                                                    <td colSpan="9" className="text-center py-5">
                                                        <div className="text-muted">
                                                            <Users size={48} className="mb-3" />
                                                            <h5>No sales found</h5>
                                                            <p>Create your first sale to get started</p>
                                                            <button 
                                                                className="btn btn-primary"
                                                                onClick={() => navigate('/add-sales')}
                                                            >
                                                                <PlusCircle size={16} className="me-2" />
                                                                New Sale
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                sales.map((sale) => (
                                                    <tr key={sale.id}>
                                                        <td>
                                                            <strong>{sale.invoice_no}</strong>
                                                            <br />
                                                            <small className="text-muted">
                                                                Created: {formatDate(sale.created_at)}
                                                            </small>
                                                        </td>
                                                        <td>{formatDate(sale.sale_date)}</td>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <div className="avatar avatar-sm bg-primary rounded-circle d-flex align-items-center justify-content-center text-white me-2">
                                                                    {sale.customer_name?.charAt(0).toUpperCase()}
                                                                </div>
                                                                <div>{sale.customer_name}</div>
                                                            </div>
                                                            <small className="text-muted d-block">
                                                                {sale.customer_phone || ''}
                                                            </small>
                                                        </td>
                                                        <td>
                                                            <strong>{formatCurrency(sale.grand_total)}</strong>
                                                            <small className="d-block text-muted">
                                                                Items: {sale.items_count || 0} | Qty: {sale.total_qty || 0}
                                                            </small>
                                                        </td>
                                                        <td>
                                                            <span className={getStatusBadge(sale.status)}>
                                                                {sale.status?.toUpperCase()}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className={getPaymentStatusBadge(sale.payment_status)}>
                                                                {sale.payment_status?.toUpperCase()}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="payment-info">
                                                                {sale.payment_count > 0 ? (
                                                                    <>
                                                                        <span className="badge bg-info me-1" title="Number of payments">
                                                                            {sale.payment_count} payment{sale.payment_count > 1 ? 's' : ''}
                                                                        </span>
                                                                        {sale.last_payment_date && (
                                                                            <small className="d-block text-muted">
                                                                                Last: {formatDate(sale.last_payment_date)}
                                                                                {sale.last_payment_method && (
                                                                                    <> via <span className="text-capitalize">{sale.last_payment_method}</span></>
                                                                                )}
                                                                            </small>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    <span className="text-muted">No payments</span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className={sale.due_amount > 0 ? 'text-danger fw-bold' : 'text-success'}>
                                                                {formatCurrency(sale.due_amount)}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="btn-group btn-group-sm" role="group">
                                                                <button
                                                                    className="btn btn-outline-info"
                                                                    onClick={() => handleView(sale)}
                                                                    title="View Details"
                                                                >
                                                                    <Eye size={14} />
                                                                </button>
                                                                <button
                                                                    className="btn btn-outline-primary"
                                                                    onClick={() => handleEdit(sale)}
                                                                    title="Edit"
                                                                >
                                                                    <Edit size={14} />
                                                                </button>
                                                                {/* PDF Download Button */}
                                                                <button
                                                                    className="btn btn-outline-warning"
                                                                    onClick={() => downloadSalePDF(sale)}
                                                                    title="Download PDF"
                                                                    disabled={loading}
                                                                >
                                                                    <File size={14} />
                                                                </button>
                                                                <button
                                                                    className="btn btn-outline-danger"
                                                                    onClick={() => handleDelete(sale.id)}
                                                                    title="Delete"
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {pagination.total > 0 && (
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div>
                                            Showing <strong>{indexOfFirstItem}-{indexOfLastItem}</strong> of{' '}
                                            <strong>{pagination.total}</strong> sales
                                            <span className="text-muted ms-2">(Page {pagination.page} of {pagination.totalPages})</span>
                                        </div>
                                        <nav>
                                            <ul className="pagination mb-0">
                                                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                                    <button 
                                                        className="page-link" 
                                                        onClick={handlePrevPage}
                                                        disabled={pagination.page === 1}
                                                    >
                                                        <ChevronLeft size={16} />
                                                    </button>
                                                </li>
                                                
                                                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                                    let pageNumber;
                                                    if (pagination.totalPages <= 5) {
                                                        pageNumber = i + 1;
                                                    } else if (pagination.page <= 3) {
                                                        pageNumber = i + 1;
                                                    } else if (pagination.page >= pagination.totalPages - 2) {
                                                        pageNumber = pagination.totalPages - 4 + i;
                                                    } else {
                                                        pageNumber = pagination.page - 2 + i;
                                                    }
                                                    
                                                    return (
                                                        <li 
                                                            key={pageNumber} 
                                                            className={`page-item ${pagination.page === pageNumber ? 'active' : ''}`}
                                                        >
                                                            <button 
                                                                className="page-link" 
                                                                onClick={() => handlePageChange(pageNumber)}
                                                            >
                                                                {pageNumber}
                                                            </button>
                                                        </li>
                                                    );
                                                })}
                                                
                                                <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                                    <button 
                                                        className="page-link" 
                                                        onClick={handleNextPage}
                                                        disabled={pagination.page === pagination.totalPages}
                                                    >
                                                        <ChevronRight size={16} />
                                                    </button>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Modals */}
                {modalType === 'view' && selectedSale && (
                    <ViewSale
                        sale={selectedSale}
                        onClose={handleModalClose}
                    />
                )}

                {modalType === 'edit' && selectedSale && (
                    <EditSale
                        saleId={selectedSale.id}
                        onSuccess={handleSuccess}
                        onClose={handleModalClose}
                        isOpen={true}
                    />
                )}
            </div>
        </div>
    );
};

export default SalesList;